(() => {
  'use strict';
  if (customElements.get('lb-lens-picker')) return;

  class LabellaLensPicker extends HTMLElement {
    // Keep order quantities in boxes. A box cannot contain two different powers.
    static buildLines(config, state, server, colorPosition, powerPosition) {
      const clear = config.kind === 'clear';
      if (clear && (colorPosition !== 0 || powerPosition !== 1 || ![6, 30].includes(config.packSize))) {
        throw new Error('Ürün seçimi doğrulanamadı. Lütfen sayfayı yenileyin.');
      }
      const color = clear ? null : config.colors.find((item) => item.id === state.color);
      if (!clear && !color) throw new Error('Lütfen bir renk seçin.');
      const productLabel = color?.label || config.label || 'Lens';
      const quantity = Number(state.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        throw new Error('Kutu adedini 1 veya daha büyük bir tam sayı olarak girin.');
      }
      const powered = powerPosition > 0;
      const powerIds = powered ? [state.first] : [null];
      if (powered && state.mode === 'different') powerIds.push(state.second);
      if (powered && powerIds.some((id) => !id)) throw new Error('Lütfen göz numaranızı seçin.');
      if (!server) throw new Error('Seçiminiz doğrulanıyor…');
      const lines = new Map();
      for (const [index, powerId] of powerIds.entries()) {
        const power = powered ? config.powers.find((item) => item.id === powerId) : null;
        const entry = powered ? server.powers.find((item) => item.id === powerId) : null;
        const variant = powered ? entry?.variant : server.selected;
        const rule = (powered ? entry?.rule : server.selectedRule) || {};
        if (powered && !power) throw new Error('Göz numaranız geçerli değil. Lütfen tekrar seçin.');
        if (!variant) throw new Error(`${productLabel}${power ? ' / ' + power.label : ''} seçeneği bulunmuyor.`);
        if ((!clear && variant.options?.[colorPosition - 1] !== color.label) ||
            (powered && variant.options?.[powerPosition - 1] !== power.label) ||
            (clear && variant.options?.length !== 1)) {
          throw new Error('Ürün ve numara eşleşmesi doğrulanamadı. Lütfen tekrar deneyin.');
        }
        if (!variant.available) throw new Error(`${productLabel}${power ? ' / ' + power.label : ''} şu anda stokta yok.`);
        if (!Number.isFinite(variant.price) || variant.price < 0 || !variant.id) {
          throw new Error('Ürün fiyatı doğrulanamadı. Lütfen tekrar deneyin.');
        }
        const minimum = Number(rule.min || 1);
        const increment = Number(rule.increment || 1);
        const maximum = rule.max == null ? null : Number(rule.max);
        if (quantity < minimum || quantity % increment !== 0 || (maximum !== null && quantity > maximum)) {
          throw new Error(`${power?.label || productLabel} için kutu adedi ürünün satış sınırlarına uygun değil.`);
        }
        const id = String(variant.id);
        if (!lines.has(id)) {
          lines.set(id, { id, quantity, price: variant.price, power: power?.label || '', eye: index === 0 ? 'Sağ göz' : 'Sol göz' });
        }
      }
      const result = [...lines.values()];
      // Equal powers are one box per requested quantity, even in different-eye mode.
      if (result.length === 1) result[0].eye = '';
      return result;
    }

    static buildFormData(source, lines) {
      const result = new FormData();
      for (const [key, value] of source.entries()) {
        if (/^items\[/.test(key)) throw new Error('Ürün formunda başka bir paket alanı var. Lütfen mağaza ile iletişime geçin.');
        if (key === 'id' || key === 'quantity' || key === 'selling_plan') continue;
        if (lines.length > 1 && /^properties\[.*\]$/.test(key)) continue;
        result.append(key, value);
      }
      if (lines.length === 1) {
        result.set('id', lines[0].id);
        result.set('quantity', String(lines[0].quantity));
      } else {
        lines.forEach((line, index) => {
          result.append(`items[${index}][id]`, line.id);
          result.append(`items[${index}][quantity]`, String(line.quantity));
          for (const [key, value] of source.entries()) {
            const match = /^properties\[(.*)\]$/.exec(key);
            if (match) result.append(`items[${index}][properties][${match[1]}]`, value);
          }
          result.set(`items[${index}][properties][Göz]`, line.eye);
        });
      }
      return result;
    }

    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-lb-config]').textContent);
        this.info = this.closest('product-info');
        this.colorPosition = Number(this.dataset.colorPosition);
        this.powerPosition = Number(this.dataset.powerPosition);
        this.clear = this.config.kind === 'clear';
        const clearProfile = { '9305569788213': 30, '8145373200693': 6 };
        if (this.clear) {
          if (this.colorPosition !== 0 || this.powerPosition !== 1 ||
              clearProfile[this.dataset.productId] !== this.config.packSize) return;
        } else if (!this.config.colors?.length || !this.colorPosition) return;
        if (!this.info) return;
        this.packSize = this.clear ? this.config.packSize : 2;
        // Keep plano products native even if an obsolete snippet is installed.
        if (!(this.powerPosition > 0) || !this.config.powers?.length) return;
        await Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('product-form')]);
        if (!this.isConnected || !this.info.querySelector('product-form form')) return;
        if (typeof this.info.handleUpdateProductInfo !== 'function') return;
        // Never show a two-box total while an unpatched form can add only one.
        if (this.info.querySelector('product-form').lbLensPickerVersion !== 1) return;
        this.state = {
          color: this.clear ? '' : (this.config.colors.find((item) => item.selected) || this.config.colors[0]).id,
          first: '', second: '', mode: 'same', quantity: 1,
        };
        this.money = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: this.config.currency });
        this.makeInterface();
        this.addEventListener('click', this.onClick = this.handleClick.bind(this));
        this.addEventListener('change', this.onChange = this.handleChange.bind(this));
        this.addEventListener('input', this.onInput = (event) => {
          if (event.target.matches('[data-lb-quantity]') && !this.locked) {
            this.state.quantity = event.target.value;
            this.renderState();
          }
        });
        this.setAttribute('data-ready', '');
        this.info.setAttribute('data-lb-ready', '');
        this.hidden = false;
        this.refresh();
      } catch (error) {
        console.error('Labella lens picker:', error);
      }
    }

    disconnectedCallback() {
      this.requestController?.abort();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.info?.removeAttribute('data-lb-ready');
      this.initialized = false;
    }

    element(tag, attributes = {}, text = '') {
      const node = document.createElement(tag);
      for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);
      if (text) node.textContent = text;
      return node;
    }

    makeInterface() {
      // Build with DOM text nodes; product option names never become HTML.
      [...this.children].filter((node) => node.tagName !== 'SCRIPT').forEach((node) => node.remove());
      if (!this.clear) {
      const colors = this.element('fieldset');
      colors.append(this.element('legend', {}, 'Renk'));
      const colorGrid = this.element('div', { class: 'lb-colors' });
      for (const color of this.config.colors) {
        const button = this.element('button', { type: 'button', class: 'lb-color', 'data-lb-color': color.id, 'aria-pressed': 'false' });
        if (color.image) button.append(this.element('img', { src: color.image, alt: '', width: '80', height: '80', loading: 'lazy' }));
        button.append(this.element('span', {}, color.label));
        colorGrid.append(button);
      }
      colors.append(colorGrid);
      this.append(colors);
      }
      if (this.powerPosition) {
        const note = this.clear
          ? `${this.config.period} şeffaf lens · 1 kutuda aynı numaradan ${this.packSize} lens bulunur. İki gözünüzün numarası aynıysa tek kutudan kullanabilirsiniz; farklıysa her numaradan ayrı kutu seçmeniz gerekir.`
          : '1 kutuda aynı numaradan 2 lens bulunur. İki gözünüzün numarası aynıysa 1 kutu, farklıysa her numaradan 1 kutu gerekir.';
        this.append(this.element('p', { class: 'lb-note' }, note));
        const mode = this.element('fieldset');
        mode.append(this.element('legend', {}, 'Göz numaralarınız'));
        for (const [value, label] of [['same', 'İki gözümün numarası aynı'], ['different', 'İki gözümün numarası farklı']]) {
          const row = this.element('label', { class: 'lb-mode' });
          const radio = this.element('input', { type: 'radio', name: `lb-mode-${this.dataset.sectionId}`, value, 'data-lb-mode': '' });
          radio.checked = value === 'same';
          row.append(radio, document.createTextNode(label));
          mode.append(row);
        }
        this.append(mode);
        this.powerRow = this.element('div', { class: 'lb-powers' });
        this.powerFields = [];
        for (const key of ['first', 'second']) {
          const row = this.element('div');
          const id = `lb-${key}-${this.dataset.sectionId}`;
          const label = this.element('label', { for: id, class: 'lb-field-label' });
          const select = this.element('select', { id, 'data-lb-power': key, 'aria-required': 'true' });
          select.append(this.element('option', { value: '' }, 'Numara seçin'));
          for (const power of this.config.powers) select.append(this.element('option', { value: power.id }, power.label));
          row.append(label, select);
          this.powerRow.append(row);
          this.powerFields.push({ row, label, select });
        }
        this.append(this.powerRow);
      }
      const quantity = this.element('div', { class: 'lb-quantity' });
      const id = `lb-quantity-${this.dataset.sectionId}`;
      this.quantityLabel = this.element('label', { for: id, class: 'lb-field-label' });
      this.quantityInput = this.element('input', { type: 'number', id, min: '1', step: '1', value: '1', inputmode: 'numeric', 'data-lb-quantity': '' });
      quantity.append(this.quantityLabel, this.quantityInput);
      this.append(quantity);
      this.summary = this.element('div', { class: 'lb-summary', 'aria-live': 'polite', 'aria-atomic': 'true' });
      this.total = this.element('strong');
      this.detail = this.element('div', { class: 'lb-detail' });
      this.summary.append(this.total, this.detail);
      this.append(this.summary);
      this.append(this.element('p', { class: 'lb-discount-note' }, 'Kampanya indirimleri ve kargo, sepette hesaplanır.'));
      this.status = this.element('p', { class: 'lb-status', role: 'status', 'aria-live': 'polite', tabindex: '-1' });
      this.retry = this.element('button', { type: 'button', class: 'lb-retry', 'data-lb-retry': '', hidden: '' }, 'Tekrar kontrol et');
      this.append(this.status, this.retry);
    }

    handleClick(event) {
      if (this.locked) return;
      const color = event.target.closest('[data-lb-color]');
      if (color && this.contains(color) && color.dataset.lbColor !== this.state.color) {
        this.state.color = color.dataset.lbColor;
        // Preserve the customer's powers; re-resolve both against the new color.
        this.refresh();
      }
      if (event.target.closest('[data-lb-retry]')) this.refresh();
    }

    handleChange(event) {
      if (this.locked) return;
      const target = event.target;
      if (target.hasAttribute('data-lb-mode')) {
        this.state.mode = target.value;
        this.refresh();
      } else if (target.hasAttribute('data-lb-power')) {
        this.state[target.dataset.lbPower] = target.value;
        this.refresh();
      }
    }

    async refresh() {
      const version = this.version = (this.version || 0) + 1;
      this.requestController?.abort();
      const controller = this.requestController = new AbortController();
      this.pending = true;
      this.server = null;
      this.networkError = '';
      this.setAttribute('aria-busy', 'true');
      this.renderState();
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
      try {
        const values = [];
        if (this.colorPosition > 0) values[this.colorPosition - 1] = this.state.color;
        if (this.powerPosition) values[this.powerPosition - 1] = this.state.first || this.config.powers[0].id;
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('option_values', values.join(','));
        const response = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Ürün bilgisi alınamadı. Lütfen tekrar deneyin.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (version !== this.version || !this.isConnected) return false;
        const node = html.querySelector(`lb-lens-picker[data-product-id="${this.dataset.productId}"] [data-lb-server]`);
        if (!node) throw new Error('Ürün seçimi doğrulanamadı. Lütfen sayfayı yenileyin.');
        const server = JSON.parse(node.textContent);
        if (server.productId !== this.dataset.productId || !Array.isArray(server.powers)) {
          throw new Error('Ürün bilgisi doğrulanamadı. Lütfen tekrar deneyin.');
        }
        // Keep Dawn's prices, variant ids and app events, while retaining the
        // shopper's position in the lens form instead of scrolling to the image.
        if (server.selected) this.applyNativeProductInfo(html);
        if (version !== this.version || !this.isConnected) return false;
        this.server = server;
        this.updatePowerAvailability();
        this.pending = false;
        this.renderState();
        return this.canSubmit();
      } catch (error) {
        if (version !== this.version || !this.isConnected) return false;
        if (error.name === 'AbortError' && !timedOut) return false;
        this.pending = false;
        this.networkError = timedOut ? 'Kontrol zaman aşımına uğradı. Lütfen tekrar deneyin.' : error.message;
        this.renderState();
        return false;
      } finally {
        clearTimeout(timer);
        if (version === this.version) this.removeAttribute('aria-busy');
      }
    }

    applyNativeProductInfo(html) {
      const info = this.info;
      const gallery = info.querySelector('media-gallery');
      const updateMedia = info.updateMedia;
      const setActiveMedia = gallery?.setActiveMedia;
      const ownsUpdateMedia = Object.prototype.hasOwnProperty.call(info, 'updateMedia');
      const ownsSetActiveMedia = gallery && Object.prototype.hasOwnProperty.call(gallery, 'setActiveMedia');
      const color = this.state.color;
      const colorChanged = this.syncedMediaColor !== color;

      // Dawn calls updateMedia for every variant update, including SPH changes.
      // Its setActiveMedia schedules window.scrollTo. Intercept only this
      // synchronous lens update; restore the original methods even on errors.
      try {
        info.updateMedia = (source, mediaId) => {
          if (!colorChanged || !mediaId || typeof updateMedia !== 'function') return;
          if (gallery) {
            gallery.setActiveMedia = (id, prepend) => this.activateMediaWithoutPageScroll(gallery, id, prepend);
          }
          updateMedia.call(info, source, mediaId);
        };
        info.handleUpdateProductInfo(info.dataset.url)(html);
        this.syncedMediaColor = color;
      } finally {
        if (ownsUpdateMedia) info.updateMedia = updateMedia;
        else delete info.updateMedia;
        if (gallery) {
          if (ownsSetActiveMedia) gallery.setActiveMedia = setActiveMedia;
          else delete gallery.setActiveMedia;
        }
      }
    }

    activateMediaWithoutPageScroll(gallery, mediaId, prepend) {
      const viewer = gallery.elements?.viewer || gallery.querySelector('[id^="GalleryViewer"]');
      if (!viewer) return;
      const items = [...viewer.querySelectorAll('[data-media-id]')];
      const active = items.find((item) => item.dataset.mediaId === String(mediaId));
      if (!active) return;
      const thumbnails = gallery.elements?.thumbnails || gallery.querySelector('[id^="GalleryThumbnails"]');
      const thumbnail = thumbnails && [...thumbnails.querySelectorAll('[data-target]')]
        .find((item) => item.dataset.target === String(mediaId));

      items.forEach((item) => item.classList.toggle('is-active', item === active));
      if (prepend) {
        if (active.parentElement.firstElementChild !== active) active.parentElement.prepend(active);
        if (thumbnail && thumbnail.parentElement.firstElementChild !== thumbnail) thumbnail.parentElement.prepend(thumbnail);
        viewer.resetPages?.();
      }
      // Only the image/thumbnail strips move horizontally. Never scroll the
      // document or focus a gallery element during a lens selection.
      active.parentElement.scrollLeft = active.offsetLeft;
      gallery.preventStickyHeader?.();
      gallery.playActiveMedia?.(active);
      if (thumbnail) {
        if (typeof gallery.setActiveThumbnail === 'function') gallery.setActiveThumbnail(thumbnail);
        else {
          thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
          thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
        }
        gallery.announceLiveRegion?.(active, thumbnail.dataset.mediaPosition);
      }
    }

    updatePowerAvailability() {
      if (!this.powerPosition) return;
      for (const { select } of this.powerFields) {
        for (const option of select.options) {
          if (!option.value) continue;
          const power = this.config.powers.find((item) => item.id === option.value);
          const variant = this.server.powers.find((item) => item.id === option.value)?.variant;
          option.disabled = !variant?.available;
          option.textContent = `${power.label}${variant?.available ? '' : ' — Stokta yok'}`;
        }
      }
    }

    getLines() {
      return LabellaLensPicker.buildLines(this.config, this.state, this.server, this.colorPosition, this.powerPosition);
    }

    canSubmit() {
      if (this.pending || this.networkError) return false;
      try { return this.getLines().length > 0; } catch { return false; }
    }

    renderState() {
      if (!this.state) return;
      this.querySelectorAll('[data-lb-color]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.lbColor === this.state.color)));
      const different = this.powerPosition && this.state.mode === 'different';
      if (this.powerPosition) {
        this.powerRow.toggleAttribute('data-single', !different);
        this.powerFields[0].label.textContent = different ? 'Sağ göz / SPH' : 'Numara / SPH';
        this.powerFields[1].label.textContent = 'Sol göz / SPH';
        this.powerFields[1].row.hidden = !different;
        this.powerFields[0].select.value = this.state.first;
        this.powerFields[1].select.value = this.state.second;
      }
      const distinct = different && this.state.first && this.state.second && this.state.first !== this.state.second;
      this.quantityLabel.textContent = distinct ? 'Her numaradan kutu adedi' : 'Kutu adedi';
      this.retry.hidden = !this.networkError;
      let error = this.networkError;
      let lines;
      if (!error && !this.pending) {
        try { lines = this.getLines(); } catch (failure) { error = failure.message; }
      }
      if (lines) {
        const boxes = lines.reduce((sum, line) => sum + line.quantity, 0);
        const total = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
        this.total.textContent = `${boxes} kutu · Ürün toplamı ${this.money.format(total / 100)}`;
        if (this.powerPosition) {
          this.detail.textContent = lines.length === 1
            ? `${boxes * this.packSize} lens · Aynı numara: ${lines[0].power}`
            : `${boxes * this.packSize} lens · Sağ: ${lines[0].power} (${lines[0].quantity} kutu), Sol: ${lines[1].power} (${lines[1].quantity} kutu)`;
        } else {
          this.detail.textContent = `${boxes * this.packSize} adet numarasız lens`;
        }
      } else {
        this.total.textContent = this.pending ? 'Seçiminiz kontrol ediliyor…' : 'Seçiminizi tamamlayın';
        this.detail.textContent = this.powerPosition ? 'Numarayı seçtiğinizde kutu adedi ve toplam tutar burada görünecek.' : '';
      }
      this.status.textContent = this.pending ? 'Güncel stok ve fiyat kontrol ediliyor…' : error || 'Seçiminiz sepete eklenebilir.';
      const incomplete = this.powerPosition && (!this.state.first || (different && !this.state.second));
      this.status.toggleAttribute('data-error', Boolean(error && !incomplete));
      this.syncSubmitButton();
    }

    syncSubmitButton() {
      const button = this.info.querySelector('product-form [type="submit"]');
      if (!button) return;
      const allowed = this.canSubmit() && !this.locked;
      button.disabled = !allowed;
      const label = button.querySelector('span');
      if (label) label.textContent = this.pending ? 'Kontrol ediliyor…' : allowed || this.locked ? (window.variantStrings?.addToCart || 'Sepete ekle') : 'Seçiminizi tamamlayın';
      // The native quantity remains present for theme/app code, but custom quantities
      // are the sole source for the cart payload in this mode.
      const nativeQuantity = this.info.querySelector('.quantity__input');
      if (nativeQuantity) nativeQuantity.value = '1';
    }

    reportSelectionError() {
      this.renderState();
      const field = this.powerFields?.find(({ select, row }) => !row.hidden && !select.value)?.select;
      (field || this.status).focus();
    }

    async prepareFormData(source) {
      this.locked = true;
      this.querySelectorAll('button, input, select').forEach((node) => { node.disabled = true; });
      // Revalidate once more immediately before add; inventory can change while
      // the product page is open. The cart endpoint remains the final authority.
      const valid = await this.refresh();
      if (!valid) throw new Error(this.networkError || this.status.textContent);
      return LabellaLensPicker.buildFormData(source, this.getLines());
    }

    unlock() {
      this.locked = false;
      this.info.querySelector('product-form [type="submit"]')?.removeAttribute('aria-disabled');
      this.querySelectorAll('button, input, select').forEach((node) => { node.disabled = false; });
      if (this.server) this.updatePowerAvailability();
      this.renderState();
    }
  }

  customElements.define('lb-lens-picker', LabellaLensPicker);
})();
